import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./Header.scss";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import LoginForm from "../SignIn/LoginForm";
import RegisterForm from "../Registration/RegisterForm";
import ForgotPasswordForm from "../SignIn/ForgotPasswordForm";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  LOGIN_URL,
  REGISTER_URL,
  FORGOT_PASSWORD_URL,
  IS_AUTHENTICATED_URL,
  RESEND_VERIFICATION_URL,
} from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import { Check, Mail } from "lucide-react";
import { pushGTMEvent } from "../../analytics/gtm";

function Header({ logo, className = "" }) {
  const { isLoggedIn, isAdmin, userEmail, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [errorMessageRegistration, setErrorMessageRegistration] = useState("");
  const [errorMessageLogin, setErrorMessageLogin] = useState("");
  const [errorMessageForgot, setErrorMessageForgot] = useState("");
  const [registrationinng, setRegistrationing] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [forgotting, setForgetting] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("idle"); // idle | loading | success | error
  const [resendMessage, setResendMessage] = useState("");

  // widgety pre registraciu, prihlasenie a zabudnute heslo
  const [captchaTokenLogin, setCaptchaTokenLogin] = useState(null);
  const [captchaTokenRegister, setCaptchaTokenRegister] = useState(null);
  const [captchaTokenForgot, setCaptchaTokenForgot] = useState(null);

  const links = [
    { label: "O nás", to: "/o-nas" },
    { label: "Rozvrh", to: "/rozvrh" },
    { label: "Cenník", to: "/cennik" },
    { label: "Kontakt", to: "/kontakt" },
  ];

  useEffect(() => {
    setErrorMessageForgot("");
    setErrorMessageLogin("");
    setErrorMessageRegistration("");
    setLoggingIn(false);
    setRegistrationing(false);
    setForgetting(false);
    if (!registerOpen) setRegisterSuccess(false);
    if (!forgotOpen) setForgotSuccess(false);
  }, [registerOpen, loginOpen]);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) setIsOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function handleLogin(params) {
    if (!captchaTokenLogin) {
      console.error(
        "Login error:",
        new Error("Captcha token není k dispozici."),
      );
      return;
    }
    await axios
      .post(LOGIN_URL, {
        email: params?.email ?? "",
        password: params?.password ?? "",
        cfTurnstileToken: captchaTokenLogin,
      })
      .then((res) => {
        window.localStorage.setItem("authToken", res.data.token);
        setErrorMessageLogin("");
        login(
          res.data.admin === true,
          res.data.email ?? null,
          res.data.id ?? null,
        );
        setLoginOpen(false);
        setLoggingIn(false);
        setCaptchaTokenLogin(null);
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.status === 400 &&
          err.response.data.type !== undefined &&
          err.response.data.type === "verification"
        ) {
          setResendEmail(params?.email ?? "");
          setLoginOpen(false);
          setResendOpen(true);
          setResendStatus("idle");
          setResendMessage("");
          return;
        }
        setLoggingIn(false);
        const msg = handleApiError(err, "Chyba pri prihlásení. Skontrolujte údaje.");
        setErrorMessageLogin(msg);
      });
  }

  async function handleIsLoggedIn() {
    const token = window.localStorage.getItem("authToken");
    if (!token) {
      return;
    }

    await axios
      .get(IS_AUTHENTICATED_URL, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("Is authenticated response:", res.data);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          window.localStorage.removeItem("authToken");
          logout();
        } else {
          handleApiError(err, "Is authenticated error");
        }
      });
  }

  async function handleRegister(params) {
    if (!captchaTokenRegister) {
      console.error(
        "Registration error:",
        new Error("Captcha token není k dispozici."),
      );
      return;
    }
    await axios
      .post(REGISTER_URL, {
        email: params?.email ?? "",
        password: params?.password ?? "",
        passwordConfirmation: params?.confirmPassword ?? "",
        firstName: params?.firstName ?? "",
        lastName: params?.lastName ?? "",
        agree: params?.agree ?? false,
        cfTurnstileToken: captchaTokenRegister,
      })
      .then((res) => {
        setErrorMessageRegistration("");
        setRegisterSuccess(true);
        setRegistrationing(false);
        setCaptchaTokenRegister(null);
        pushGTMEvent("sign_up", { method: "email" });
      })
      .catch((err) => {
        setRegistrationing(false);
        const msg = handleApiError(err, "Chyba pri registrácii. Skontrolujte údaje.");
        setErrorMessageRegistration(msg);
      });
  }

  async function handleForgotPassword(params) {
    if (!/\S+@\S+\.\S+/.test(params?.email)) {
      console.error(
        "Forgot password error:",
        new Error("Neplatný formát emailu."),
      );
      return;
    }
    if (!captchaTokenForgot) {
      console.error(
        "Forgot password error:",
        new Error("Captcha token není k dispozici."),
      );
      return;
    }
    await axios
      .post(FORGOT_PASSWORD_URL, {
        email: params?.email ?? "",
        cfTurnstileToken: captchaTokenForgot,
      })
      .then((res) => {
        setForgetting(false);
        setErrorMessageForgot("");
        setForgotSuccess(true);
      })
      .catch((err) => {
        setForgetting(false);
        const msg = handleApiError(err, "Chyba pri odosielaní nového hesla. Skúste to znova.");
        setErrorMessageForgot(msg);
      });
  }

  return (
    <>
      <header className={`header ${className}`}>
        <div className="header__container">
          <Link to="/" className="header__logo">
            {logo || "Logo"}
          </Link>

          <nav className={`header__nav ${isOpen ? "header__nav--open" : ""}`}>
            {links.map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className={`header__link ${location.pathname === link.to ? "header__link--active" : ""}`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="header__actions">
              {isLoggedIn ? (
                <>
                  {userEmail && (
                    <span className="header__user-email">{userEmail}</span>
                  )}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsOpen(false);
                        navigate("/admin");
                      }}
                    >
                      Admin Panel
                    </Button>
                  )}
                  {!isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsOpen(false);
                        navigate("/profil");
                      }}
                    >
                      Môj profil
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                  >
                    Odhlásiť sa
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setLoginOpen(true)}
                  >
                    Prihlásiť sa
                  </Button>
                  {/* <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setRegisterOpen(true)}
                  >
                    Registrácia
                  </Button> */}
                </>
              )}
            </div>
          </nav>

          <button
            className="header__burger"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>

          <div
            className={`header__overlay ${isOpen ? "header__overlay--visible" : ""}`}
            onClick={() => setIsOpen(false)}
          />
        </div>
      </header>

      {/* Login Modal */}
      <Modal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        title="Prihlásenie"
      >
        <LoginForm
          onSubmit={(data) => {
            handleLogin(data);
          }}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setRegisterOpen(true);
          }}
          onForgotPassword={() => {
            setLoginOpen(false);
            setForgotOpen(true);
          }}
          errorMessage={errorMessageLogin}
          loggingIn={loggingIn}
          setLoggingIn={setLoggingIn}
          setCaptchaToken={setCaptchaTokenLogin}
        />
      </Modal>

      {/* Register Modal */}
      <Modal
        isOpen={registerOpen}
        onClose={() => setRegisterOpen(false)}
        title={registerSuccess ? "Registrácia úspešná" : "Registrácia"}
      >
        {registerSuccess ? (
          <div className="header__register-success">
            <div className="header__register-success-icon">
              <Check />
            </div>
            <p className="header__register-success-text">
              Registrácia prebehla úspešne! Na váš email sme odoslali overovací
              odkaz. Po overení sa môžete prihlásiť.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setRegisterOpen(false);
                setLoginOpen(true);
              }}
            >
              Prejsť na prihlásenie
            </Button>
          </div>
        ) : (
          <RegisterForm
            onSubmit={(data) => {
              handleRegister(data);
            }}
            onSwitchToLogin={() => {
              setRegisterOpen(false);
              setLoginOpen(true);
            }}
            errorMessage={errorMessageRegistration}
            registrationinng={registrationinng}
            setRegistrationing={setRegistrationing}
            setCaptchaToken={setCaptchaTokenRegister}
          />
        )}
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title={forgotSuccess ? "Email odoslaný" : "Zabudnuté heslo"}
      >
        {forgotSuccess ? (
          <div className="header__register-success">
            <div className="header__register-success-icon">
              <Check />
            </div>
            <p className="header__register-success-text">
              Nové heslo bolo odoslané na váš email. Skontrolujte svoju schránku
              a prihláste sa.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setForgotOpen(false);
                setLoginOpen(true);
              }}
            >
              Prejsť na prihlásenie
            </Button>
          </div>
        ) : (
          <ForgotPasswordForm
            onSubmit={(data) => {
              handleForgotPassword(data);
            }}
            onBackToLogin={() => {
              setForgotOpen(false);
              setLoginOpen(true);
            }}
            errorMessage={errorMessageForgot}
            forgotting={forgotting}
            setForgetting={setForgetting}
            setCaptchaToken={setCaptchaTokenForgot}
          />
        )}
      </Modal>

      {/* Resend Verification Modal */}
      <Modal
        isOpen={resendOpen}
        onClose={() => setResendOpen(false)}
        title="Overenie emailu"
      >
        <div className="header__resend-verify">
          {resendStatus === "success" ? (
            <>
              <div className="header__resend-verify-icon header__resend-verify-icon--success">
                <Check />
              </div>
              <p className="header__resend-verify-text">{resendMessage}</p>
              <Button
                variant="primary"
                onClick={() => {
                  setResendOpen(false);
                  setLoginOpen(true);
                }}
              >
                Prejsť na prihlásenie
              </Button>
            </>
          ) : (
            <>
              <div className="header__resend-verify-icon">
                <Mail />
              </div>
              <p className="header__resend-verify-text">
                Váš email ešte nebol overený. Kliknite na tlačidlo nižšie a
                pošleme vám nový overovací odkaz na{" "}
                <strong>{resendEmail}</strong>.
              </p>
              {resendStatus === "error" && (
                <p className="header__resend-verify-error">{resendMessage}</p>
              )}
              <Button
                variant="primary"
                disabled={resendStatus === "loading"}
                onClick={async () => {
                  setResendStatus("loading");
                  await axios
                    .post(RESEND_VERIFICATION_URL, { email: resendEmail })
                    .then(() => {
                      setResendStatus("success");
                      setResendMessage(
                        "Overovací email bol úspešne odoslaný! Skontrolujte svoju schránku.",
                      );
                    })
                    .catch((err) => {
                      setResendStatus("error");
                      const msg = handleApiError(err, "Nepodarilo sa odoslať overovací email. Skúste to znova.");
                      setResendMessage(msg);
                    });
                }}
              >
                {resendStatus === "loading"
                  ? "Odosiela sa..."
                  : "Odoslať overovací email"}
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

export default Header;
